import { RouterAdminType, RouterAuthType, RouterNameType, StaticPageName } from '@/core/data/config/uiConfig.interface';
import { gambits } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import AuthImages from '@/views/auth/pages/AuthImages.vue';
import { createRouter, createWebHistory } from 'vue-router';

import AuthView from '@/views/auth/AuthView.vue';
import AuthLocation from '@/views/auth/pages/AuthLocation.vue';
import AuthMail from '@/views/auth/pages/AuthMail.vue';
import AuthOtp from '@/views/auth/pages/AuthOtp.vue';
import AuthRatings from '@/views/auth/pages/AuthRatings.vue';
import AuthRegister from '@/views/auth/pages/AuthRegistration.vue';
import AuthSelection from '@/views/auth/pages/AuthSelection.vue';
import ChessboardView from '@/views/main/chessboard/ChessboardView.vue';
import GambitsView from '@/views/main/gambits/GambitsView.vue';
import GambitView from '@/views/main/gambits/GambitView.vue';
import LocationsView from '@/views/main/locations/LocationsView.vue';
import NotificationsView from '@/views/main/notifications/NotificationsView.vue';
import PlayersView from '@/views/main/players/PlayersView.vue';
import { denyInAppProfileVisit } from '@/views/main/players/profile-access';
import ProfileView from '@/views/main/profile/ProfileView.vue';
import SparkIgnition from '@/views/main/sparks/SparkIgnition.vue';
import WalkHistoryOverlay from '@/views/main/walk/modals/WalkHistoryOverlay.vue';
import WalkView from '@/views/main/walk/WalkView.vue';
import RootView from '@/views/RootView.vue';
import StaticPage from '@/views/StaticPage.vue';
import UsernameView from '@/views/UsernameView.vue';

// const MainView = () => import('@/views/MainView.vue');
const AdminView = () => import('@/views/admin/AdminView.vue');
const AdminLocations = () => import('@/views/admin/AdminLocations.vue');
const AdminUsers = () => import('@/views/admin/AdminUsers.vue');
const AdminUsernames = () => import('@/views/admin/AdminUsernames.vue');
const AdminReports = () => import('@/views/admin/AdminReports.vue');
const AdminBoards = () => import('@/views/admin/AdminBoards.vue');

const DuelView = () => import('@/views/main/duel/DuelView.vue');

const ClockView = () => import('@/views/main/clock/ClockView.vue');

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'active',
  linkExactActiveClass: 'active-exact',
  routes: [
    {
      path: '/',
      name: RouterNameType.ROOT,
      component: RootView,
    },
    {
      path: '/players/:username?',
      name: RouterNameType.PLAYERS,
      component: PlayersView,
    },
    {
      path: '/clubs/:username?',
      redirect: '/',
    },
    {
      path: '/gambit/:id',
      name: RouterNameType.GAMBIT,
      component: GambitView,
    },
    {
      path: '/gambits/:id?',
      name: RouterNameType.GAMBITS,
      component: GambitsView,
    },
    {
      path: '/sparks/:slide?',
      name: RouterNameType.SPARKS,
      component: SparkIgnition,
    },
    {
      path: '/news',
      name: RouterNameType.NEWS,
      component: NotificationsView,
    },
    {
      path: '/discover',
      name: RouterNameType.LOCATIONS,
      component: LocationsView,
    },
    {
      path: '/spot/:id',
      redirect: (to) => ({ name: RouterNameType.LOCATIONS, query: { spot: to.params.id as string } }),
    },
    {
      path: '/walk',
      name: RouterNameType.WALK,
      component: WalkView,
    },
    {
      path: '/walks',
      name: RouterNameType.WALKS,
      component: WalkHistoryOverlay,
    },
    {
      path: '/duel/:id?',
      name: RouterNameType.DUEL,
      component: DuelView,
    },
    {
      path: '/duels',
      name: RouterNameType.DUELS,
      redirect: { name: RouterNameType.WALK },
    },
    {
      path: '/squares',
      name: RouterNameType.SQUARES,
      redirect: { name: RouterNameType.WALK },
    },
    {
      path: '/clock',
      name: RouterNameType.CLOCK,
      component: ClockView,
    },
    {
      path: '/profile',
      name: RouterNameType.PROFILE,
      component: ProfileView,
      beforeEnter: (to, from, next) => {
        next();
      },
    },
    {
      path: '/auth',
      name: RouterAuthType.AUTH,
      component: AuthView,
      children: [
        {
          path: '',
          name: RouterAuthType.AUTH_SELECT,
          component: AuthSelection,
        },
        {
          path: 'email',
          name: RouterAuthType.AUTH_EMAIL,
          component: AuthMail,
        },
        {
          path: 'otp',
          name: RouterAuthType.AUTH_OTP,
          component: AuthOtp,
        },
        {
          path: 'register',
          name: RouterAuthType.AUTH_REGISTRATION,
          component: AuthRegister,
        },
        {
          path: 'ratings',
          name: RouterAuthType.AUTH_RATINGS,
          component: AuthRatings,
          beforeEnter: (to, from, next) => {
            next();
          },
        },
        {
          path: 'location',
          name: RouterAuthType.AUTH_LOCATION,
          component: AuthLocation,
          beforeEnter: (to, from, next) => {
            next();
          },
        },
        {
          path: 'profile-images',
          name: RouterAuthType.AUTH_PROFILE_IMAGES,
          component: AuthImages,
          beforeEnter: (to, from, next) => {
            next();
          },
        },
      ],
    },
    {
      path: '/terms-and-conditions',
      name: StaticPageName.TERMS,
      component: StaticPage,
    },
    {
      path: '/privacy',
      name: StaticPageName.PRIVACY_POLICY,
      component: StaticPage,
    },
    {
      path: '/imprint',
      name: StaticPageName.IMPRINT,
      component: StaticPage,
    },
    {
      path: '/contact',
      name: StaticPageName.CONTACT,
      component: StaticPage,
    },
    {
      path: '/admin',
      name: RouterNameType.ADMIN,
      component: AdminView,
      redirect: { name: RouterAdminType.USERS },
      children: [
        {
          path: 'locations',
          name: RouterAdminType.LOCATIONS,
          component: AdminLocations,
        },
        {
          path: 'users',
          name: RouterAdminType.USERS,
          component: AdminUsers,
        },
        {
          path: 'usernames',
          name: RouterAdminType.USERNAMES,
          component: AdminUsernames,
        },
        {
          path: 'reports',
          name: RouterAdminType.REPORTS,
          component: AdminReports,
        },
        {
          path: 'boards',
          name: RouterAdminType.BOARDS,
          component: AdminBoards,
        },
      ],
    },
    {
      path: '/board/:id?',
      name: RouterNameType.BOARD,
      component: ChessboardView,
    },
    {
      path: '/@:username',
      name: RouterNameType.USERNAME,
      component: UsernameView,
      beforeEnter: async (to, from, next) => {
        const usernameParam = to.params.username;
        const username = Array.isArray(usernameParam) ? usernameParam[0] : usernameParam;
        if (!username) {
          next();
          return;
        }

        const authStore = useAuthStore();
        const denied = await denyInAppProfileVisit({
          username,
          fromName: from.name,
          isAdmin: authStore.isAdmin(),
          ownUsername: authStore.userDetails?.username,
        });

        if (denied) {
          next(false);
          return;
        }

        next();
      },
    },
  ],
});

const STATIC_ROUTES = [
  StaticPageName.CONTACT,
  StaticPageName.IMPRINT,
  StaticPageName.PRIVACY_POLICY,
  StaticPageName.TERMS,
];

const AUTH_ENTRY_ROUTES = new Set<RouterAuthType>([
  RouterAuthType.AUTH_SELECT,
  RouterAuthType.AUTH_EMAIL,
  RouterAuthType.AUTH_OTP,
]);

router.beforeEach(async (to, from, next) => {
  const isLoggedIn = gambits.authService.isAuthenticated();
  const isOnboarded = gambits.authService.isActive();
  const isAdmin = gambits.authService.isAdmin();
  const toName = to?.name?.toString() || '';
  const isAuthRoute = Object.values(RouterAuthType).includes(toName as RouterAuthType);
  const isAdminRoute = Object.values(RouterAdminType).includes(toName as RouterAdminType);
  const isStaticPage = STATIC_ROUTES.includes(toName as StaticPageName);

  // Restrict access to admin routes
  if (isAdminRoute && !isAdmin) {
    next({ name: RouterNameType.SPARKS });
    return;
  }

  // Ensure onboarding is completed
  if (isLoggedIn && !isOnboarded && !isAuthRoute) {
    next({ name: RouterAuthType.AUTH_REGISTRATION });
    return;
  }

  // Ensure user is logged in for restricted screens. The clock keeps no data and
  // belongs to nobody, so like the board it is usable without an account.
  const isPublicRoute = isStaticPage || toName === RouterNameType.BOARD || toName === RouterNameType.CLOCK;
  if (!isLoggedIn && !isAuthRoute && !isPublicRoute) {
    next({ name: RouterAuthType.AUTH_SELECT, query: { redirect: to.fullPath } });
    return;
  }

  // Persisted user city is required for authenticated navigation.
  if (isLoggedIn) {
    const authStore = useAuthStore();

    if (!authStore.userDetails?.id) {
      await authStore.fetchUserInfo();
    }

    const isAuthLocationRoute = toName === RouterAuthType.AUTH_LOCATION;
    const isAuthEntryRoute = AUTH_ENTRY_ROUTES.has(toName as RouterAuthType);
    const userInfoLoaded = !!authStore.userDetails?.id;
    const userCityId = authStore.userDetails?.city?.id || null;
    const isActive = authStore.user?.isActive;

    if (userInfoLoaded && !userCityId && !isAuthLocationRoute && !isAuthEntryRoute && isActive) {
      next({ name: RouterAuthType.AUTH_LOCATION });
      return;
    }
  }

  next();
});
export default router;
